import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  ShoppingBag, Package, Clock, CheckCircle, Truck, XCircle, Eye,
  Euro, TrendingUp, Users, BarChart3, Calendar, Mail, Phone, MapPin, StickyNote
} from "lucide-react";

interface Order {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  delivery_method: string;
  delivery_address: string;
  notes: string;
  status: string;
  total_amount: number;
  created_at: string;
}

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmada",
  shipped: "Enviada",
  delivered: "Entregue",
  cancelled: "Cancelada",
};

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  shipped: "bg-purple-100 text-purple-800 border-purple-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const statusIcons: Record<string, typeof Clock> = {
  pending: Clock,
  confirmed: CheckCircle,
  shipped: Truck,
  delivered: Package,
  cancelled: XCircle,
};

const deliveryLabels: Record<string, string> = {
  pickup: "Retirar em mãos",
  delivery: "Entrega ao domicílio",
  massage_pickup: "Receber na massagem",
};

const OrdersAdmin = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOrders = async () => {
    const { data } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
    const all = (data as Order[]) || [];
    setAllOrders(all);
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
    const onFocus = () => fetchOrders();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  // Filter orders client-side
  useEffect(() => {
    if (filter === "all") {
      setOrders(allOrders);
    } else {
      setOrders(allOrders.filter(o => o.status === filter));
    }
  }, [filter, allOrders]);

  // Stats
  const stats = useMemo(() => {
    const active = allOrders.filter(o => o.status !== "cancelled");
    const totalRevenue = active.filter(o => o.status === "delivered").reduce((s, o) => s + Number(o.total_amount), 0);
    const pendingRevenue = active.filter(o => ["pending", "confirmed", "shipped"].includes(o.status)).reduce((s, o) => s + Number(o.total_amount), 0);
    const totalOrders = allOrders.length;
    const uniqueClients = new Set(allOrders.map(o => o.client_email.toLowerCase())).size;
    const thisMonth = allOrders.filter(o => {
      const d = new Date(o.created_at);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return { totalRevenue, pendingRevenue, totalOrders, uniqueClients, thisMonth: thisMonth.length };
  }, [allOrders]);

  const openOrder = async (order: Order) => {
    setSelected(order);
    const { data } = await supabase.from("order_items").select("*").eq("order_id", order.id);
    setItems((data as OrderItem[]) || []);
  };

  const updateStatus = async (id: string, status: string) => {
    await supabase.from("orders").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
    toast({ title: `Encomenda ${statusLabels[status]?.toLowerCase() || status}!` });
    fetchOrders();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const filters = ["all", "pending", "confirmed", "shipped", "delivered", "cancelled"];
  const filterLabels: Record<string, string> = { all: "Todas", ...statusLabels };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Vendas & Encomendas</h1>
        <p className="text-muted-foreground">Acompanhe todas as vendas da loja virtual</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center">
              <Euro size={20} className="text-green-700" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Receita Total</p>
              <p className="text-lg font-bold text-foreground">€{stats.totalRevenue.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <TrendingUp size={20} className="text-yellow-700" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Receita Pendente</p>
              <p className="text-lg font-bold text-foreground">€{stats.pendingRevenue.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <ShoppingBag size={20} className="text-blue-700" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Encomendas</p>
              <p className="text-lg font-bold text-foreground">{stats.totalOrders}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Users size={20} className="text-purple-700" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Clientes Únicos</p>
              <p className="text-lg font-bold text-foreground">{stats.uniqueClients}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filters.map(f => {
          const count = f === "all" ? allOrders.length : allOrders.filter(o => o.status === f).length;
          return (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              className="rounded-full text-xs gap-1.5"
              onClick={() => setFilter(f)}
            >
              {filterLabels[f]}
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 h-4 rounded-full">
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Orders Table */}
      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <ShoppingBag size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Nenhuma encomenda encontrada</p>
            <p className="text-sm">As encomendas aparecerão aqui quando os clientes comprarem na loja.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Entrega</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map(o => {
                const StatusIcon = statusIcons[o.status] || Clock;
                return (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openOrder(o)}>
                    <TableCell className="whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-muted-foreground" />
                        {new Date(o.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{o.client_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1"><Mail size={12} />{o.client_email}</span>
                        <span className="flex items-center gap-1"><Phone size={12} />{o.client_phone}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{deliveryLabels[o.delivery_method] || o.delivery_method}</TableCell>
                    <TableCell className="text-right font-bold">€{Number(o.total_amount).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[o.status] || "bg-muted text-foreground"} text-xs gap-1`}>
                        <StatusIcon size={12} />
                        {statusLabels[o.status] || o.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); openOrder(o); }}>
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  <Package size={20} className="text-primary" />
                  Detalhes da Encomenda
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-2">
                <TabsList className="w-full">
                  <TabsTrigger value="details" className="flex-1">Resumo</TabsTrigger>
                  <TabsTrigger value="products" className="flex-1">Produtos</TabsTrigger>
                  <TabsTrigger value="client" className="flex-1">Cliente</TabsTrigger>
                </TabsList>

                {/* Summary Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <Badge className={`${statusColors[selected.status]} text-sm gap-1`}>
                      {React.createElement(statusIcons[selected.status] || Clock, { size: 14 })}
                      {statusLabels[selected.status]}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(selected.created_at).toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>

                  <Card>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Método de entrega</span>
                        <span className="font-medium">{deliveryLabels[selected.delivery_method]}</span>
                      </div>
                      {selected.delivery_address && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1"><MapPin size={14} /> Morada</span>
                          <span className="font-medium text-right max-w-[60%]">{selected.delivery_address}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t border-border pt-3">
                        <span className="font-bold text-foreground">Total</span>
                        <span className="font-bold text-primary text-lg">€{Number(selected.total_amount).toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {selected.notes && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                          <StickyNote size={16} className="text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Notas do cliente</p>
                            <p className="text-sm">{selected.notes}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {selected.status === "pending" && (
                      <>
                        <Button className="gap-1.5 rounded-full" onClick={() => updateStatus(selected.id, "confirmed")}>
                          <CheckCircle size={14} /> Confirmar Encomenda
                        </Button>
                        <Button variant="destructive" className="gap-1.5 rounded-full" onClick={() => updateStatus(selected.id, "cancelled")}>
                          <XCircle size={14} /> Cancelar
                        </Button>
                      </>
                    )}
                    {selected.status === "confirmed" && (
                      <Button className="gap-1.5 rounded-full" onClick={() => updateStatus(selected.id, "shipped")}>
                        <Truck size={14} /> Marcar como Enviada
                      </Button>
                    )}
                    {selected.status === "shipped" && (
                      <Button className="gap-1.5 rounded-full" onClick={() => updateStatus(selected.id, "delivered")}>
                        <CheckCircle size={14} /> Marcar como Entregue
                      </Button>
                    )}
                  </div>
                </TabsContent>

                {/* Products Tab */}
                <TabsContent value="products" className="mt-4">
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Preço Unit.</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map(item => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">€{Number(item.unit_price).toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium">€{(item.unit_price * item.quantity).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-t-2">
                          <TableCell colSpan={3} className="font-bold text-right">Total</TableCell>
                          <TableCell className="text-right font-bold text-primary text-lg">€{Number(selected.total_amount).toFixed(2)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>

                {/* Client Tab */}
                <TabsContent value="client" className="mt-4">
                  <Card>
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users size={22} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-bold text-lg text-foreground">{selected.client_name}</p>
                          <p className="text-xs text-muted-foreground">Cliente</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                          <Mail size={16} className="text-muted-foreground" />
                          <a href={`mailto:${selected.client_email}`} className="text-primary hover:underline">{selected.client_email}</a>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <Phone size={16} className="text-muted-foreground" />
                          <a href={`tel:${selected.client_phone}`} className="text-primary hover:underline">{selected.client_phone}</a>
                        </div>
                        {selected.delivery_address && (
                          <div className="flex items-center gap-3 text-sm">
                            <MapPin size={16} className="text-muted-foreground" />
                            <span>{selected.delivery_address}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersAdmin;
